import StatusEditor from '@/components/global/settings/StatusEditor';
import { ManageSettingItemsDialog } from '@/components/settings/ManageSettingItemsDialog';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { CALCULATION_REQUEST_STAGES, STAGED_SETTING_KEYS } from '@/lib/calculation-request-stages';
import { router } from '@inertiajs/react';
import { Pencil } from 'lucide-react';
import { useState } from 'react';

interface ItemTranslations {
    en: Record<string, string>;
    lt: Record<string, string>;
}

interface ObjectFormSetting {
    key: string;
    value: unknown;
    translation_group?: string | null;
    item_translations?: ItemTranslations | null;
}

interface ObjectFormSettingEditorProps {
    setting: ObjectFormSetting;
    onUpdate: (key: string, value: string | number | boolean | object | null) => void;
}

/**
 * `object_form` settings (statuses, priorities — rows of {value,label,color}) edited
 * through StatusEditor, plus the per-language label dialog the tag-input settings
 * already get from MultiSelectSettingDropdown.
 *
 * StatusEditor only edits the English `label` stored in the settings JSON, so
 * without this an admin had no way to give these values a Lithuanian label.
 */
export default function ObjectFormSettingEditor({ setting, onUpdate }: ObjectFormSettingEditorProps) {
    const { t } = useTranslations();
    const [manageOpen, setManageOpen] = useState(false);

    const translationGroup = setting.translation_group;
    const requiresStage = (STAGED_SETTING_KEYS as readonly string[]).includes(setting.key);

    const handleSuccess = () => {
        // Reload settings + translations so edited labels show immediately.
        router.reload({ only: ['settings', 'translations'] });
    };

    return (
        <div className="flex flex-col gap-2">
            {translationGroup ? (
                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setManageOpen(true)}
                        title={t('settings.manage_items.title', 'Manage items & translations')}
                    >
                        <Pencil className="mr-2 h-4 w-4" />
                        {t('settings.manage_items.translations', 'Translations')}
                    </Button>
                </div>
            ) : null}

            <StatusEditor
                setting={setting as never}
                onUpdate={onUpdate}
                // Calculation-request statuses drive a workflow, so each one has to be
                // classified into a lifecycle stage. Other status settings have no such
                // concept and get no picker.
                stages={requiresStage ? CALCULATION_REQUEST_STAGES : undefined}
                stageLabel={(stage) => t(`calculation_requests.stages.${stage}`, stage)}
            />

            {translationGroup ? (
                <ManageSettingItemsDialog
                    open={manageOpen}
                    onOpenChange={setManageOpen}
                    settingKey={setting.key}
                    setting={setting}
                    onSuccess={handleSuccess}
                />
            ) : null}
        </div>
    );
}
